import React, { useState, useEffect } from 'react';
import { Dumbbell, Mail, Eye, User, Loader2, CheckCircle, AlertTriangle, Sun, Moon } from 'lucide-react';
import { ArrowLeftIcon } from './icons';
import { toast } from 'sonner';
import { authClient } from '../lib/authClient';
import { useThemeStore } from '../store/useThemeStore';

const API_BASE_URL = import.meta.env.PROD
  ? ''
  : (import.meta.env.VITE_API_URL || 'http://localhost:3001');

interface Props {
  initialMode?: 'login' | 'register';
  onBack?: () => void;
}

export default function LoginScreen({ initialMode = 'login', onBack }: Props) {
  const [mode, setMode] = useState<'login' | 'register'>(initialMode);
  const [loading, setLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const { dark, toggle } = useThemeStore();

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'trainer' | 'client'>('client');

  // Load verification state from session storage on mount
  useEffect(() => {
    const savedEmail = sessionStorage.getItem('verificationEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setVerificationSent(true);
    }
  }, []);

  // Cooldown timer effect
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Wprowadź email i hasło');
      return;
    }

    setLoading(true);
    try {
      const { error } = await authClient.signIn.email({
        email,
        password,
      }, {
        onError: (ctx) => {
          if (ctx.error.status === 403) {
            // Email not verified
            sessionStorage.setItem('verificationEmail', email);
            setVerificationSent(true);
            toast.error('Zweryfikuj swój adres e-mail. Sprawdź skrzynkę pocztową.');
          } else {
            toast.error(ctx.error.message || 'Błąd logowania');
          }
        },
      });

      if (error) {
        if (error.status !== 403) {
          toast.error(error.message || 'Błąd logowania');
        }
      } else {
        sessionStorage.removeItem('verificationEmail');
        setVerificationSent(false);
        toast.success('Zalogowano pomyślnie!');
        // Force a reload/navigation to ensure the app picks up the new session immediately
        window.location.href = '/';
      }
    } catch (error: any) {
      toast.error(error.message || 'Błąd logowania');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || !name) {
      toast.error('Wypełnij wszystkie pola');
      return;
    }

    if (password.length < 8) {
      toast.error('Hasło musi mieć minimum 8 znaków');
      return;
    }

    if (!acceptTerms) {
      toast.error('Musisz zaakceptować Regulamin i Politykę prywatności');
      return;
    }

    setLoading(true);
    try {
      const { error } = await authClient.signUp.email({
        email,
        password,
        name,
        role,
      } as any); // Type cast might be needed if strictly typed without inferring additionalFields

      if (error) {
        toast.error(error.message || 'Błąd rejestracji');
        setLoading(false);
        return;
      }

      sessionStorage.setItem('verificationEmail', email);
      setVerificationSent(true);
      toast.success('Konto utworzone! Sprawdź e-mail aby zweryfikować.');
    } catch (error: any) {
      toast.error(error.message || 'Błąd rejestracji');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      toast.error('Wprowadź adres e-mail');
      return;
    }
    if (resendCooldown > 0) {
      toast.error(`Poczekaj ${resendCooldown}s przed kolejną próbą`);
      return;
    }

    try {
      setResendCooldown(60); // Start 60s cooldown
      await authClient.sendVerificationEmail({
        email,
        callbackURL: '/',
      });
      toast.success('E-mail weryfikacyjny wysłany ponownie!');
    } catch {
      toast.error('Nie udało się wysłać e-maila');
      setResendCooldown(0); // Reset cooldown on error
    }
  };

  // Verification sent view
  if (verificationSent) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col items-center justify-center p-4 relative">
        <button
          onClick={toggle}
          className="absolute top-4 right-4 p-2 rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-800/50 transition-all"
        >
          {dark ? <Sun size={20} /> : <Moon size={20} />}
        </button>
        <div className="w-full max-w-md animate-fade-in">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-green-100 dark:bg-green-950 mb-4">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight mb-2 text-slate-900 dark:text-slate-100">
              Sprawdź swoją skrzynkę e-mail
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs mx-auto">
              Wysłaliśmy link weryfikacyjny na <strong className="text-slate-700 dark:text-slate-300">{email}</strong>.
              Kliknij go, aby aktywować konto.
            </p>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-6 shadow-sm space-y-4">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800">
              <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800 dark:text-amber-200">
                Jeśli nie widzisz e-maila, sprawdź folder spam.
              </p>
            </div>

            <button
              onClick={handleResendVerification}
              disabled={resendCooldown > 0}
              className="w-full py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {resendCooldown > 0 ? `Wyślij ponownie za ${resendCooldown}s` : 'Wyślij ponownie e-mail weryfikacyjny'}
            </button>

            <button
              onClick={() => {
                setVerificationSent(false);
                setMode('login');
                sessionStorage.removeItem('verificationEmail');
              }}
              className="w-full py-2.5 rounded-lg text-sm font-medium text-brand-600 hover:text-brand-700 transition"
            >
              Wróć do logowania
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col items-center justify-center p-4 relative">
      <button
        onClick={toggle}
        className="absolute top-4 right-4 p-2 rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-800/50 transition-all"
      >
        {dark ? <Sun size={20} /> : <Moon size={20} />}
      </button>
      {onBack && (
        <button
          onClick={onBack}
          className="absolute top-4 left-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition font-medium"
        >
          <ArrowLeftIcon size={16} /> Powrót
        </button>
      )}
      <div className="w-full max-w-md animate-fade-in">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-100 dark:bg-brand-950 mb-4">
            <Dumbbell size={32} className="text-brand-600" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight mb-1 text-slate-900 dark:text-slate-100">
            APEX<span className="text-brand-500 ml-0.5">Builder</span>
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {mode === 'login' ? 'Zaloguj się do swojego konta' : 'Utwórz nowe konto'}
          </p>
        </div>

        {/* Auth Form */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-6 shadow-sm">
          <form onSubmit={mode === 'login' ? handleLogin : handleRegister} className="space-y-4">
            {/* Name field (only for register) */}
            {mode === 'register' && (
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                  Imię i nazwisko
                </label>
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jan Kowalski"
                    disabled={loading}
                    className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  />
                </div>
              </div>
            )}

            {/* Role field (only for register) */}
            {mode === 'register' && (
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                  Typ konta
                </label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value as 'trainer' | 'client')}
                  disabled={loading}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <option value="client">Klient</option>
                  <option value="trainer">Trener</option>
                </select>
              </div>
            )}

            {/* Email field */}
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                Email
              </label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="twoj@email.com"
                  disabled={loading}
                  autoFocus={mode === 'login'}
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed transition"
                />
              </div>
            </div>

            {/* Password field */}
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                Hasło
              </label>
              <div className="relative">
                <Eye size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === 'register' ? 'Minimum 8 znaków' : '••••••••'}
                  disabled={loading}
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed transition"
                />
              </div>
              {mode === 'register' && (
                <p className="text-xs text-slate-500 mt-1.5">
                  Hasło musi zawierać minimum 8 znaków
                </p>
              )}
            </div>

            {/* Terms acceptance checkbox (only for register) */}
            {mode === 'register' && (
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="acceptTerms"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  disabled={loading}
                  className="mt-0.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                />
                <label htmlFor="acceptTerms" className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                  Akceptuję{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:text-brand-700 underline font-medium">Regulamin</a>
                  {' '}oraz{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:text-brand-700 underline font-medium">Politykę prywatności</a>
                </label>
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {mode === 'login' ? 'Zaloguj się' : 'Utwórz konto'}
            </button>
          </form>

          {/* Mode toggle */}
          <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 text-center">
            <p className="text-xs text-slate-500">
              {mode === 'login' ? 'Nie masz jeszcze konta?' : 'Masz już konto?'}
              <button
                onClick={() => {
                  setMode(mode === 'login' ? 'register' : 'login');
                  setPassword('');
                }}
                disabled={loading}
                className="ml-1 text-brand-600 hover:text-brand-700 font-medium underline disabled:opacity-50 disabled:no-underline"
              >
                {mode === 'login' ? 'Zarejestruj się' : 'Zaloguj się'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
